import { type CSSProperties, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { HStack, VStack } from "@/shared/ui/Stack";
import { EXPORT_MENU_ITEMS, MENU_GAP, MENU_WIDTH, VIEWPORT_MARGIN } from "./constants";
import styles from "./ExportMenu.module.css";
import type { ExportMenuProps } from "./types";

type MenuPosition = {
    top: number;
    left: number;
};

export function ExportMenu({ anchorRef, menuRef, onExport }: ExportMenuProps) {
    const { t } = useTranslation();
    const [position, setPosition] = useState<MenuPosition | null>(null);

    useLayoutEffect(() => {
        const updatePosition = () => {
            const anchor = anchorRef.current;
            if (!anchor) {
                return;
            }

            const anchorRect = anchor.getBoundingClientRect();
            const menu = menuRef.current;
            const menuWidth = menu?.offsetWidth || MENU_WIDTH;
            const maxLeft = window.innerWidth - menuWidth - VIEWPORT_MARGIN;
            const left = Math.max(VIEWPORT_MARGIN, Math.min(anchorRect.left, maxLeft));
            const top = anchorRect.bottom + MENU_GAP;

            setPosition({ top, left });
        };

        updatePosition();
    }, [anchorRef, menuRef]);

    if (typeof document === "undefined") {
        return null;
    }

    const menuStyle: CSSProperties = {
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? "visible" : "hidden",
    };

    return createPortal(
        <VStack
            ref={menuRef}
            align="stretch"
            className={styles.exportMenu}
            data-ui-root
            style={menuStyle}
        >
            {EXPORT_MENU_ITEMS.map(({ icon: Icon, label, format }) => (
                <button
                    key={label}
                    type="button"
                    className={styles.exportMenuItem}
                    onClick={() => onExport(format)}
                >
                    <HStack as="span" fullWidth gap={10}>
                        <Icon />
                        <span>{`${t("export.title")} ${label}`}</span>
                    </HStack>
                </button>
            ))}
        </VStack>,
        document.body,
    );
}
